import React, { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClipboardCheck,
  faPaperPlane,
  faSpinner,
  faTimes,
  faUser,
  faCalendarAlt,
  faFlag,
  faListCheck,
  faSlidersH,
  faAlignLeft,
  faPaperclip,
  faFile,
  faFileImage,
  faFilePdf,
  faDownload,
  faXmark,
  faEye,
  faChevronDown,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import {
  normalizeTaskProgressPercent,
  resolveTaskProgressState,
  TASK_PRIORITY_OPTIONS,
  TASK_STATUS_OPTIONS,
  toDateInputValue,
} from "../taskUtils";
import { buildMediaUrl } from "../../Tickets/ticketUtils";
import { deleteTaskFile } from "../taskService";

const BRAND_LR = "linear-gradient(to right, #C04C2A, #4A1B13)";
const BRAND_DIAG = "linear-gradient(135deg, #C04C2A 0%, #8B2A15 55%, #4A1B13 100%)";

const PRIORITY_COLORS = {
  Low:    { bg: "#ecfdf5", border: "#6ee7b7", text: "#065f46" },
  Medium: { bg: "#fffbeb", border: "#fcd34d", text: "#92400e" },
  High:   { bg: "#fff7ed", border: "#fb923c", text: "#9a3412" },
  Urgent: { bg: "#fff1f2", border: "#fda4af", text: "#9f1239" },
};

const buildDefaultValues = ({ ticket, identity, employees, initialValues }) => {
  if (initialValues) {
    return {
      title: initialValues.title || "",
      description: initialValues.description || "",
      priority: initialValues.priority || "Medium",
      status: initialValues.status || "Pending",
      progressPercent: normalizeTaskProgressPercent(initialValues.progressPercent),
      dueDate: initialValues.dueDate ? String(initialValues.dueDate).split("T")[0] : "",
      assignedEmployeeId: String(initialValues.assignedEmployeeId || ""),
      observerEmployeeIds: (initialValues.observers || []).map((o) => Number(o.employeeId)).filter(Boolean),
    };
  }
  const selfEmployee = (employees || []).find(
    (item) => String(item.userId || "") === String(identity?.userId || "")
  );
  return {
    title: ticket?.subject ? `Follow up: ${ticket.subject}` : "",
    description: ticket?.description
      ? `Task generated from support ticket ${ticket.ticketNumber || ""}.\n\nCustomer issue:\n${ticket.description}`
      : "",
    priority: ticket?.priority || "Medium",
    status: "Pending",
    progressPercent: 0,
    dueDate: toDateInputValue(new Date(Date.now() + 24 * 60 * 60 * 1000)),
    assignedEmployeeId: selfEmployee?.id ? String(selfEmployee.id) : "",
    observerEmployeeIds: [],
  };
};

export default function TaskComposerModal({
  open, ticket, employees, identity, initialValues,
  submitting, mode = "create", onClose, onSubmit,
}) {
  const [formState, setFormState] = useState(() =>
    buildDefaultValues({ ticket, identity, employees, initialValues })
  );
  const [errors, setErrors] = useState({});
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileError, setFileError] = useState("");
  const [existingFiles, setExistingFiles] = useState(() => initialValues?.files || initialValues?.Files || []);
  const [deletingFileId, setDeletingFileId] = useState(null);
  const [observerSearch, setObserverSearch] = useState("");

  useEffect(() => {
    if (!open) return;
    setFormState(buildDefaultValues({ ticket, identity, employees, initialValues }));
    setErrors({});
    setSelectedFiles([]);
    setFileError("");
    setExistingFiles(initialValues?.files || initialValues?.Files || []);
    setDeletingFileId(null);
    setObserverSearch("");
  }, [open, ticket, identity, employees, initialValues]);

  const assignee = useMemo(
    () => (employees || []).find((item) => String(item.id) === String(formState.assignedEmployeeId || "")) || null,
    [employees, formState.assignedEmployeeId]
  );

  const canEditProgress =
    mode !== "edit" ||
    String(initialValues?.assignedUserId || "") === String(identity?.userId || "");

  const pct = formState.progressPercent;
  const priorityColor = PRIORITY_COLORS[formState.priority] || PRIORITY_COLORS.Medium;

  if (!open) return null;

  const validate = () => {
    const e = {};
    if (!formState.title.trim()) e.title = "Task title is required.";
    if (!formState.description.trim()) e.description = "Task details are required.";
    if (!formState.assignedEmployeeId) e.assignedEmployeeId = "Choose an employee.";
    if (!formState.dueDate) e.dueDate = "Due date is required.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSubmit({
      ...resolveTaskProgressState(formState.status, formState.progressPercent),
      title: formState.title.trim(),
      description: formState.description.trim(),
      priority: formState.priority,
      dueDate: formState.dueDate,
      assignedEmployeeId: Number(formState.assignedEmployeeId || 0),
      assignedUserId: assignee?.userId || "",
      assignedEmployeeName: assignee?.fullName || "",
      assignedEmployeeEmail: assignee?.workEmail || "",
      files: selectedFiles,
      observerEmployeeIds: formState.observerEmployeeIds || [],
    });
  };

  const setField = (key, value) => setFormState((cur) => ({ ...cur, [key]: value }));

  const toggleObserver = (employeeId) => {
    setFormState((cur) => {
      const current = cur.observerEmployeeIds || [];
      const exists = current.includes(employeeId);
      return {
        ...cur,
        observerEmployeeIds: exists
          ? current.filter((id) => id !== employeeId)
          : [...current, employeeId],
      };
    });
  };

  const observerOptions = (employees || []).filter((emp) => String(emp.id) !== String(formState.assignedEmployeeId || ""));
  const filteredObserverOptions = observerSearch.trim()
    ? observerOptions.filter((emp) =>
        `${emp.fullName} ${emp.employeeCode}`.toLowerCase().includes(observerSearch.trim().toLowerCase())
      )
    : observerOptions;

  const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB per file

  const handleFilesPicked = (fileList) => {
    const picked = Array.from(fileList || []);
    if (!picked.length) return;
    const tooLarge = picked.filter((f) => f.size > MAX_FILE_SIZE);
    const accepted = picked.filter((f) => f.size <= MAX_FILE_SIZE);
    setFileError(tooLarge.length ? `${tooLarge.map((f) => f.name).join(", ")} exceed(s) the 20MB limit and were skipped.` : "");
    if (accepted.length) setSelectedFiles((cur) => [...cur, ...accepted]);
  };

  const removeSelectedFile = (idx) => setSelectedFiles((cur) => cur.filter((_, i) => i !== idx));

  const handleDeleteExistingFile = async (file) => {
    if (!file?.id || deletingFileId) return;
    if (!window.confirm(`Delete "${file.fileName}"? This cannot be undone.`)) return;

    setDeletingFileId(file.id);
    setFileError("");
    try {
      await deleteTaskFile(file.id);
      setExistingFiles((cur) => cur.filter((f) => f.id !== file.id));
    } catch (err) {
      setFileError(err?.message || "File could not be deleted.");
    } finally {
      setDeletingFileId(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const fileIconFor = (name = "") => {
    if (/\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(name)) return faFileImage;
    if (/\.pdf$/i.test(name)) return faFilePdf;
    return faFile;
  };

  const handleProgressChange = (val) => {
    const resolved = resolveTaskProgressState(formState.status, val);
    setFormState((cur) => ({ ...cur, status: resolved.status, progressPercent: resolved.progressPercent }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-3">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal shell */}
      <div
        className="relative z-10 flex w-full flex-col overflow-hidden bg-white dark:bg-gray-900 shadow-2xl sm:rounded-[1.25rem]"
        style={{
          maxWidth: 880,
          maxHeight: "96dvh",
          borderRadius: "1.25rem 1.25rem 0 0",
        }}
      >
        {/* ── Header ── */}
        <div className="relative shrink-0 overflow-hidden px-4 py-4 text-white sm:px-6" style={{ background: BRAND_DIAG }}>
          <div className="pointer-events-none absolute -right-4 -top-4 h-24 w-24 rounded-full bg-white/5 blur-2xl" />

          <button
            type="button"
            onClick={onClose}
            className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full bg-white/15 text-white transition hover:bg-white/25"
          >
            <FontAwesomeIcon icon={faTimes} className="text-xs" />
          </button>

          <div className="pr-8">
            <div className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-orange-100">
              <FontAwesomeIcon icon={faClipboardCheck} />
              {mode === "edit" ? "Update Task" : "New Task"}
            </div>
            <h3 className="mt-1.5 text-base font-extrabold tracking-tight text-white sm:text-lg">
              {mode === "edit" ? "Update task details" : "Create a new task"}
            </h3>
            <p className="mt-0.5 text-[11px] text-orange-200">
              {ticket?.ticketNumber
                ? <>Ticket: <span className="font-semibold text-white">{ticket.ticketNumber}</span></>
                : "Fill in the details below"}
            </p>
          </div>

          {/* Progress strip */}
          <div className="mt-3 h-1 overflow-hidden rounded-full bg-white/15">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${pct}%`, background: pct === 100 ? "#6ee7b7" : "rgba(255,255,255,0.65)" }}
            />
          </div>
          <p className="mt-0.5 text-right text-[10px] text-orange-200">{pct}% complete</p>
        </div>

        {/* ── Body (scrollable) ── */}
        <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-8 sm:py-6">
          <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-12">

            {/* Title */}
            <div className="col-span-1 sm:col-span-12">
              <Field label="Task Title" icon={faListCheck} required error={errors.title}>
                <input
                  value={formState.title}
                  onChange={(e) => setField("title", e.target.value)}
                  placeholder="e.g. Verify invoice and call customer"
                  className="fi"
                />
              </Field>
            </div>

            {/* Assign To */}
            <div className="col-span-1 sm:col-span-6">
              <Field label="Assign To" icon={faUser} required error={errors.assignedEmployeeId}>
                <SearchableSelect
                  options={employees || []}
                  value={formState.assignedEmployeeId}
                  onChange={(id) => setField("assignedEmployeeId", id)}
                  placeholder="Select employee"
                />
              </Field>
            </div>

            {/* Observers */}
            <div className="col-span-1 sm:col-span-6">
              <Field label="Observers (optional)" icon={faEye}>
                <ObserverDropdown
                  options={observerOptions}
                  search={observerSearch}
                  onSearchChange={setObserverSearch}
                  filteredOptions={filteredObserverOptions}
                  selectedIds={formState.observerEmployeeIds || []}
                  onToggle={toggleObserver}
                  employees={employees}
                />
              </Field>
            </div>

            {/* Priority */}
            <div className="col-span-1 sm:col-span-4">
              <Field label="Priority" icon={faFlag}>
                <div className="flex flex-wrap gap-1.5">
                  {TASK_PRIORITY_OPTIONS.map((p) => {
                    const c = PRIORITY_COLORS[p];
                    const active = formState.priority === p;
                    return (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setField("priority", p)}
                        className={`rounded-lg border px-3 py-1 text-xs font-bold transition-all${active ? "" : " bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500"}`}
                        style={active ? {
                          background: c.bg,
                          borderColor: c.border,
                          color: c.text,
                          boxShadow: `0 0 0 2px ${c.border}50`,
                        } : undefined}
                      >
                        {p}
                      </button>
                    );
                  })}
                </div>
              </Field>
            </div>

            {/* Status */}
            <div className="col-span-1 sm:col-span-4">
              <Field label="Status" icon={faSlidersH}>
                {mode === "create" ? (
                  <input value={formState.status} readOnly disabled className="fi" />
                ) : (
                  <select
                    value={formState.status}
                    onChange={(e) => {
                      const nextStatus = e.target.value;
                      const resolved = resolveTaskProgressState(nextStatus, nextStatus === "Completed" ? 100 : formState.progressPercent);
                      setFormState((cur) => ({ ...cur, status: resolved.status, progressPercent: resolved.progressPercent }));
                    }}
                    className="fi"
                  >
                    {TASK_STATUS_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                )}
              </Field>
            </div>

            {/* Due Date */}
            <div className="col-span-1 sm:col-span-4">
              <Field label="Due Date" icon={faCalendarAlt} required error={errors.dueDate}>
                <input type="date" value={formState.dueDate} onChange={(e) => setField("dueDate", e.target.value)} className="fi" />
              </Field>
            </div>

            {/* Progress */}
            <div className="col-span-1 sm:col-span-12">
              <Field label="Progress" icon={faSlidersH}>
                <div className="rounded-xl border border-slate-100 bg-slate-50 dark:border-gray-700 dark:bg-gray-800/50 px-3 py-2.5">
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-slate-500 dark:text-gray-400">Completion</span>
                    <span className="font-extrabold" style={{ color: pct === 100 ? "#10b981" : "#C04C2A" }}>{pct}%</span>
                  </div>
                  <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-200 dark:bg-gray-700">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${pct}%`, background: pct === 100 ? "linear-gradient(to right,#34d399,#10b981)" : BRAND_LR }}
                    />
                  </div>
                  <input
                    type="range" min="0" max="100" step="5" value={pct}
                    disabled={!canEditProgress}
                    onChange={(e) => handleProgressChange(e.target.value)}
                    className={`mt-2 w-full ${!canEditProgress ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                    style={{ accentColor: "#C04C2A" }}
                  />
                  <div className="mt-1.5 flex items-center gap-2">
                    <input
                      type="number" min="0" max="100" value={pct}
                      disabled={!canEditProgress}
                      onChange={(e) => handleProgressChange(e.target.value)}
                      className="w-16 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-semibold outline-none disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:disabled:bg-gray-900"
                    />
                    <span className="text-[11px] text-slate-400">
                      {canEditProgress ? "100% auto-completes task." : "Only the active assignee can change progress."}
                    </span>
                  </div>
                </div>
              </Field>
            </div>

            {/* Task Details */}
            <div className="col-span-1 sm:col-span-12">
              <Field label="Task Details" icon={faAlignLeft} required error={errors.description}>
                <textarea
                  value={formState.description}
                  onChange={(e) => setField("description", e.target.value)}
                  placeholder="Add work details, customer context, expected result, and next steps."
                  rows={3}
                  className="fi resize-none"
                />
              </Field>
            </div>

            {/* Attachments */}
            <div className="col-span-1 sm:col-span-12">
              <Field label="Attachments" icon={faPaperclip} error={fileError}>
                <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-3 py-3 text-xs font-semibold text-slate-500 transition hover:border-orange-300 hover:bg-orange-50/50 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-300">
                  <FontAwesomeIcon icon={faPaperclip} style={{ color: "#C04C2A" }} />
                  Click to attach files (multiple allowed)
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => { handleFilesPicked(e.target.files); e.target.value = ""; }}
                  />
                </label>

                {existingFiles.length > 0 && (
                  <div className="mt-2 space-y-1.5">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                      Already uploaded ({existingFiles.length})
                    </div>
                    {existingFiles.map((f) => (
                      <div
                        key={f.id || f.fileUrl}
                        className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs dark:border-gray-700 dark:bg-gray-800"
                      >
                        <FontAwesomeIcon icon={fileIconFor(f.fileName)} className="text-slate-400" />
                        <span className="min-w-0 flex-1 truncate text-slate-700 dark:text-gray-200">{f.fileName}</span>
                        <span className="shrink-0 text-slate-400">{formatFileSize(f.fileSize)}</span>
                        {f.replyId ? (
                          <span className="shrink-0 rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-bold text-slate-500 dark:bg-gray-700 dark:text-gray-300">Reply</span>
                        ) : null}
                        <a
                          href={buildMediaUrl(f.fileUrl)}
                          target="_blank"
                          rel="noreferrer"
                          download={f.fileName}
                          className="shrink-0 text-slate-400 hover:text-orange-600"
                        >
                          <FontAwesomeIcon icon={faDownload} />
                        </a>
                        {mode === "edit" && (
                          <button
                            type="button"
                            onClick={() => handleDeleteExistingFile(f)}
                            disabled={deletingFileId === f.id}
                            className="shrink-0 text-slate-400 hover:text-rose-600 disabled:cursor-wait disabled:opacity-50"
                            title="Delete file"
                          >
                            <FontAwesomeIcon icon={deletingFileId === f.id ? faSpinner : faXmark} spin={deletingFileId === f.id} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {selectedFiles.length > 0 && (
                  <div className="mt-2 space-y-1.5">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                      To upload ({selectedFiles.length})
                    </div>
                    {selectedFiles.map((file, idx) => (
                      <div
                        key={`${file.name}-${idx}`}
                        className="flex items-center gap-2 rounded-lg border border-orange-100 bg-orange-50/40 px-2.5 py-1.5 text-xs dark:border-orange-900/30 dark:bg-orange-900/10"
                      >
                        <FontAwesomeIcon icon={fileIconFor(file.name)} style={{ color: "#C04C2A" }} />
                        <span className="min-w-0 flex-1 truncate text-slate-700 dark:text-gray-200">{file.name}</span>
                        <span className="shrink-0 text-slate-400">{formatFileSize(file.size)}</span>
                        <button
                          type="button"
                          onClick={() => removeSelectedFile(idx)}
                          className="shrink-0 text-slate-400 hover:text-rose-600"
                        >
                          <FontAwesomeIcon icon={faXmark} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </Field>
            </div>

            {/* Created By */}
            <div className="col-span-1 sm:col-span-12">
              <div
                className="flex items-center gap-2.5 rounded-xl border px-3 py-2"
                style={{ borderColor: "rgba(192,76,42,0.18)", background: "rgba(192,76,42,0.04)" }}
              >
                <div
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
                  style={{ background: BRAND_LR }}
                >
                  {(identity?.fullName || identity?.email || "U").slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="text-[9px] font-bold uppercase tracking-widest" style={{ color: "#C04C2A" }}>Created By</div>
                  <div className="truncate text-[12px] font-semibold text-slate-800 dark:text-gray-200">{identity?.fullName || identity?.email || "Logged in user"}</div>
                  <div className="text-[10px] text-slate-400">ID: {identity?.userId || "—"}</div>
                </div>
                {/* priority badge on the right */}
                <span
                  className="ml-auto shrink-0 rounded-lg border px-2 py-0.5 text-[10px] font-bold"
                  style={{ background: priorityColor.bg, borderColor: priorityColor.border, color: priorityColor.text }}
                >
                  {formState.priority}
                </span>
              </div>
            </div>

          </div>
        </div>

        {/* ── Footer ── */}
        <div className="flex shrink-0 items-center gap-2 border-t border-slate-100 bg-slate-50 dark:border-gray-800 dark:bg-gray-900 px-4 py-3 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-xl border border-slate-200 bg-white py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 sm:flex-none sm:px-5"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl py-2 text-sm font-bold text-white shadow-sm transition hover:opacity-90 disabled:opacity-60 sm:flex-none sm:px-6"
            style={{ background: BRAND_LR }}
          >
            <FontAwesomeIcon icon={submitting ? faSpinner : faPaperPlane} spin={submitting} />
            {submitting ? "Saving…" : mode === "edit" ? "Update Task" : "Create Task"}
          </button>
        </div>
      </div>

      <style>{`
        .fi { width:100%; border-radius:0.75rem; border:1px solid #e2e8f0; background:#fff; padding:0.5rem 0.875rem; font-size:0.8125rem; color:#334155; outline:none; transition:border-color 0.15s,box-shadow 0.15s; }
        .fi:focus { border-color:#C04C2A; box-shadow:0 0 0 3px rgba(192,76,42,0.10); }
        .fi:disabled { background:#f8fafc; color:#94a3b8; cursor:not-allowed; }
        .dark .fi { background:#1f2937; border-color:#374151; color:#e5e7eb; }
        .dark .fi:focus { border-color:#C04C2A; }
        .dark .fi:disabled { background:#111827; color:#6b7280; }
      `}</style>
    </div>
  );
}

function Field({ label, icon, required, error, children }) {
  return (
    <div>
      <label className="mb-1 flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">
        {icon ? <FontAwesomeIcon icon={icon} className="text-[9px]" style={{ color: "#C04C2A" }} /> : null}
        {label}
        {required ? <span className="text-rose-500">*</span> : null}
      </label>
      {children}
      {error ? (
        <div className="mt-1 flex items-center gap-1 text-[11px] font-medium text-rose-600">
          <span className="h-1 w-1 shrink-0 rounded-full bg-rose-500" />
          {error}
        </div>
      ) : null}
    </div>
  );
}

function useCloseOnOutsideClick(open, onClose) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open, onClose]);
  return ref;
}

function SearchableSelect({ options, value, onChange, placeholder = "Select…" }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useCloseOnOutsideClick(open, () => setOpen(false));

  const selected = (options || []).find((emp) => String(emp.id) === String(value || "")) || null;
  const filtered = search.trim()
    ? (options || []).filter((emp) =>
        `${emp.fullName} ${emp.employeeCode}`.toLowerCase().includes(search.trim().toLowerCase())
      )
    : options || [];

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((cur) => !cur)}
        className="fi flex items-center justify-between gap-2 text-left"
      >
        <span className={`min-w-0 flex-1 truncate ${selected ? "text-slate-700 dark:text-gray-200" : "text-slate-400"}`}>
          {selected ? `${selected.fullName}${selected.employeeCode ? ` (${selected.employeeCode})` : ""}` : placeholder}
        </span>
        <FontAwesomeIcon icon={faChevronDown} className={`shrink-0 text-[10px] text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full z-20 mt-1.5 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
          <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2 dark:border-gray-700">
            <FontAwesomeIcon icon={faSearch} className="text-[11px] text-slate-400" />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search employees…"
              className="w-full bg-transparent text-xs outline-none dark:text-gray-200"
            />
          </div>
          <div className="max-h-40 overflow-y-auto p-1.5">
            {filtered.length === 0 ? (
              <div className="px-2 py-2 text-[11px] text-slate-400">No matching employees.</div>
            ) : (
              filtered.map((emp) => (
                <button
                  key={emp.id || emp.userId}
                  type="button"
                  onClick={() => { onChange(String(emp.id)); setOpen(false); setSearch(""); }}
                  className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs transition hover:bg-slate-50 dark:hover:bg-gray-700/50 ${
                    String(emp.id) === String(value || "") ? "bg-orange-50 dark:bg-orange-900/20" : ""
                  }`}
                >
                  <span className="min-w-0 flex-1 truncate text-slate-700 dark:text-gray-200">
                    {emp.fullName}{emp.employeeCode ? ` (${emp.employeeCode})` : ""}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ObserverDropdown({ search, onSearchChange, filteredOptions, selectedIds, onToggle, employees }) {
  const [open, setOpen] = useState(false);
  const ref = useCloseOnOutsideClick(open, () => setOpen(false));

  const selectedEmployees = (selectedIds || [])
    .map((id) => (employees || []).find((e) => Number(e.id) === id))
    .filter(Boolean);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((cur) => !cur)}
        className="fi flex items-center justify-between gap-2 text-left"
      >
        <span className={`min-w-0 flex-1 truncate ${selectedEmployees.length ? "text-slate-700 dark:text-gray-200" : "text-slate-400"}`}>
          {selectedEmployees.length
            ? `${selectedEmployees.length} observer${selectedEmployees.length > 1 ? "s" : ""} selected`
            : "Select observers"}
        </span>
        <FontAwesomeIcon icon={faChevronDown} className={`shrink-0 text-[10px] text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full z-20 mt-1.5 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
          <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2 dark:border-gray-700">
            <FontAwesomeIcon icon={faSearch} className="text-[11px] text-slate-400" />
            <input
              autoFocus
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search employees…"
              className="w-full bg-transparent text-xs outline-none dark:text-gray-200"
            />
          </div>
          <div className="max-h-40 overflow-y-auto p-1.5">
            {filteredOptions.length === 0 ? (
              <div className="px-2 py-2 text-[11px] text-slate-400">No matching employees.</div>
            ) : (
              filteredOptions.map((emp) => {
                const checked = (selectedIds || []).includes(Number(emp.id));
                return (
                  <label
                    key={emp.id || emp.userId}
                    className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-xs hover:bg-slate-50 dark:hover:bg-gray-700/50"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => onToggle(Number(emp.id))}
                      className="h-3.5 w-3.5 shrink-0 rounded border-slate-300 accent-[#C04C2A]"
                    />
                    <span className="min-w-0 flex-1 truncate text-slate-700 dark:text-gray-200">
                      {emp.fullName}{emp.employeeCode ? ` (${emp.employeeCode})` : ""}
                    </span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}

      {selectedEmployees.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {selectedEmployees.map((emp) => (
            <span
              key={emp.id}
              className="inline-flex items-center gap-1 rounded-full bg-orange-50 px-2 py-0.5 text-[10px] font-semibold text-orange-700 dark:bg-orange-900/20 dark:text-orange-300"
            >
              {emp.fullName}
              <button type="button" onClick={() => onToggle(Number(emp.id))} className="text-orange-400 hover:text-orange-700">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
